<script setup lang="ts">
import { ref } from 'vue'
import { useTeams } from '@/composables/core/teams'
import type { ITeam } from '@/types/core/Teams'
import { SearchIcon } from '@/components/common/icon/Search'
import { ElMessage } from 'element-plus'

const props = defineProps<{ team: ITeam }>()

const emailList = ref<string[]>([])

const { inviteTeamMembersMutation } = useTeams()
const { isPending, mutateAsync, isError, error } = inviteTeamMembersMutation

const open = defineModel<boolean>()
const emit = defineEmits(['closed'])

const submit = async () => {
  if (emailList.value.length == 0) {
    open.value = false
    return
  }

  await mutateAsync(
    { id: props.team.id, form: { emails: emailList.value } },
    {
      onSuccess: () => {
        open.value = false
      },
    }
  )
}

const validateNewEmail = (value: string | string[]) => {
  const email = Array.isArray(value) ? value[0] : value
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  if (!validEmail) {
    emailList.value.pop()
    ElMessage.error('Email address is not valid')
  }
}

/**
 * Reset form
 */
const handleClosed = () => {
  inviteTeamMembersMutation.reset()
  emailList.value = []
  emit('closed')
}
</script>

<template>
  <el-dialog v-model="open" :width="600" title="Invite Members" :show-close="false" @closed="handleClosed">
    <el-input-tag
      v-model="emailList"
      tag-type="success"
      clearable
      placeholder="Add all member emails"
      @add-tag="validateNewEmail"
    >
      <template #prefix>
        <el-icon><SearchIcon /></el-icon>
      </template>
    </el-input-tag>
    <rl-error-alert v-if="isError" :error="error" />
    <template #footer>
      <el-button :disabled="isPending || !open" @click="open = false">Cancel</el-button>
      <el-button type="primary" :loading="isPending" :disabled="isPending || !open" @click="submit"
        >Send invitations</el-button
      >
    </template>
  </el-dialog>
</template>
